import React from 'react';

import classNames from 'classnames';
import { NodeData } from '../interfaces/NodeData';
import config, { IConfiguration } from '../utils/Dotenv';
import MinimapStyles from '../sass/partials/_minimap.module.scss';

interface Props {
  currPanoId: string;
  onClickNode: Function;
  nodeData: NodeData[];
  sideNavOpened: boolean;
  currRotation: number;
  minimapEnlarged: boolean;
  updateMinimapEnlarged: Function;
  config: IConfiguration;
  linkNodeFunction: Function;
  timelineOpen: boolean;
  closeTimelineFunction: Function;
  minimapImage: string | undefined;
  floor?: number;
}

interface State {
  mapHover: boolean;
}

export default class Minimap extends React.Component<Props, State> {
  public state = {
    mapHover: false,
  };

  private handleNodeClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, node: NodeData): void => {
    e.stopPropagation();
    this.props.updateMinimapEnlarged(false);
    this.props.onClickNode(node.surveyNode.tilesId);
  };

  private mapHover(mapHover: boolean): void {
    this.setState({ mapHover });
  }

  private configureRotation(node: NodeData): string {
    if (!config.ENABLE_ROTATION) {
      return '';
    }

    if (node.surveyNode.tilesId === this.props.currPanoId) {
      let error_offset = 0;
      if (config.TITLE === 'Andrew N. Liveris Building') {
        if (parseInt(node.surveyNode.surveyName.split(' ')[1]) >= 69) {
          error_offset = 3.14;
        }
      }
      return `rotate(${this.props.currRotation + config.ROTATION_OFFSET + error_offset}rad)`;
    }
    return '';
  }

  private getNodesJSX(nodeData: NodeData[]): React.ReactElement[] {
    return nodeData.map(node => {
      // Element Position = Scale * (Position within map + Offset)
      let x_position: number =
        (config.X_SCALE *
          ((!config.XY_FLIPPED ? node.xPixelOffset : node.yPixelOffset) + config.X_PIXEL_OFFSET) *
          100) /
        config.IMG_WIDTH;
      let y_position: number =
        (config.Y_SCALE *
          ((!config.XY_FLIPPED ? node.yPixelOffset : node.xPixelOffset) + config.Y_PIXEL_OFFSET) *
          100) /
        config.IMG_HEIGHT;

      // ERROR: This fails when an offset is used, broken logic!!
      //      let x_position: number = config.X_SCALE
      //        * (((!config.XY_FLIPPED ? node.xPixelOffset : node.yPixelOffset) / config.IMG_WIDTH) * 100
      //          + config.X_PIXEL_OFFSET);
      //      let y_position: number = config.Y_SCALE
      //        * (((!config.XY_FLIPPED ? node.yPixelOffset : node.xPixelOffset) / config.IMG_HEIGHT) * 100
      //          + config.Y_PIXEL_OFFSET);

      if (x_position > 100) {
        x_position = 95;
      } else if (x_position < 0) {
        x_position = 5;
      }
      if (y_position > 100) {
        y_position = 95;
      } else if (y_position < 0) {
        y_position = 5;
      }

      let isMapEnlarged = this.props.minimapEnlarged;
      let isInfoNode = node.surveyNode.infoHotspots.length > 0;
      const nodeTitle = node.surveyNode.tilesName;

      return (
        <div>
          <div
            className={MinimapStyles.nodeContainer}
            style={{
              top: y_position + '%',
              left: x_position + '%',
              transform: this.configureRotation(node),
            }}
            key={node.surveyNode.tilesId}
          >
            {node.surveyNode.tilesId === this.props.currPanoId && config.ENABLE_ROTATION && (
              <div className={classNames(MinimapStyles.positionIndicator)} />
            )}

            <div
              className={classNames(MinimapStyles.node, {
                [MinimapStyles.selectedNode]: node.surveyNode.tilesId === this.props.currPanoId,
                [MinimapStyles.unselectedNode]: node.surveyNode.tilesId !== this.props.currPanoId,
                [MinimapStyles.upscaled]: this.props.minimapEnlarged,
                [MinimapStyles.scaled]: !this.props.minimapEnlarged,
                [MinimapStyles.infoNode]: node.surveyNode.infoHotspots.length,
              })}
              key={node.surveyNode.tilesId}
              id={node.surveyNode.tilesId}
              onClick={(e): void => this.handleNodeClick(e, node)}
              data-cy={node.surveyNode.tilesId === this.props.currPanoId ? 'selected-node' : 'node'}
            />
          </div>
          {isMapEnlarged && (
            <div
              className={MinimapStyles.nodeTitle}
              style={{
                top: y_position + '%',
                left: x_position + '%',
                // transform: this.configureRotation(node)
              }}
            >
              {isInfoNode && (
                <div className={MinimapStyles.infoIcon}>
                  <i className="fas fa-info-circle" />
                </div>
              )}
              {nodeTitle}
            </div>
          )}
        </div>
      );
    });
  }

  public render(): React.ReactElement {
    return (
      <div
        className={classNames(MinimapStyles.miniMapContainer, {
          [MinimapStyles.sideNavOpen]: this.props.sideNavOpened,
        })}
        data-cy="mini-map"
      >
        <div
          className={classNames({
            [MinimapStyles.largeMap]: this.props.minimapEnlarged,
            [MinimapStyles.smallMap]: !this.props.minimapEnlarged,
            [MinimapStyles.showMargin]: this.props.timelineOpen,
          })}
          onClick={(): void => {
            this.props.linkNodeFunction(false);
          }}
          onMouseOver={(): void => this.mapHover(true)}
          onMouseOut={(): void => this.mapHover(false)}
          data-cy={this.props.minimapEnlarged ? 'minimap-large' : 'minimap-small'}
        >
          <button
            className={this.props.minimapEnlarged ? MinimapStyles.openShow : MinimapStyles.closeShow}
            onClick={(): void => {
              this.props.updateMinimapEnlarged(!this.props.minimapEnlarged);
            }}
          >
            <i className={this.props.minimapEnlarged ? 'fas fa-window-close' : 'fas fa-expand-arrows-alt'} />
          </button>
          <img
            className={classNames('small-map-img', MinimapStyles.minimapImage, MinimapStyles.smallMapImg, {
              [MinimapStyles.minimapImgHover]: !this.state.mapHover,
            })}
            src={this.props.minimapImage}
            alt="Facility Minimap"
            style={{
              display: !this.props.minimapEnlarged ? 'block' : 'none',
            }}
          />
          <img
            className={classNames(MinimapStyles.minimapImage, MinimapStyles.largeMapImg, {
              [MinimapStyles.minimapImgHover]: this.state.mapHover,
            })}
            src={this.props.minimapImage}
            alt="Facility Minimap with Index"
            style={{
              display: this.props.minimapEnlarged ? 'block' : 'none',
            }}
          />
          {this.props.floor ? <h1>Level {this.props.floor} </h1> : null}
          {this.getNodesJSX(this.props.nodeData)}
        </div>
      </div>
    );
  }
}
